<!-- components/ProcessFlowViewer.vue -->
<template>
  <div class="process-flow-viewer" v-if="recordList.length > 0">
    <!-- 加载状态 -->
    <van-loading v-if="loading" class="loading" size="24px" vertical>
      加载中...
    </van-loading>

    <!-- 详情内容 -->
    <div v-else-if="recordList.length > 0" class="detail-content">

      <!-- 遍历显示每条记录 -->
      <div v-for="(record, idx) in recordList" :key="record.FlowId" class="record-card">

        <!-- 基本信息 -->
        <van-cell-group title="" class="cell-group">
          <van-cell title="工序名称" :value="record.OrderName" align="left"/>
          <van-cell title="负责人" :value="record.ImgBy" align="left"/>
          <van-cell title="日期范围" :value="getDateRange(record)" align="left"/>
        </van-cell-group>

        <!-- 详细信息（用户填写的完整内容） -->
        <van-cell-group title="描述" class="cell-group" v-if="record.Details">
          <van-cell>
            <template #default>
              <div class="detail-text">{{ record.Details }}</div>
            </template>
          </van-cell>
        </van-cell-group>

        <!-- 图片列表 - 使用 van-swipe 轮播 -->
        <van-cell-group title="现场图片" class="cell-group">
          <van-cell>
            <template #default>
              <div v-if="getImageList(record).length > 0" class="image-swipe-container">
                <van-swipe 
                  class="image-swipe" 
                  :autoplay="autoplay" 
                  indicator-color="white"
                  :show-indicators="getImageList(record).length > 1"
                  @change="(index) => onSwipeChange(record.flowId, index)"
                >
                  <van-swipe-item v-for="(image, imgIndex) in getImageList(record)" :key="imgIndex">
                    <div class="swipe-image-wrapper" @click="previewImg(getImageList(record), imgIndex)" >
                      <img :src="image" class="swipe-image" />
                      <div class="image-index">{{ imgIndex + 1 }} / {{ getImageList(record).length }}</div>
                    </div>
                  </van-swipe-item>
                </van-swipe>
              </div>
              <div v-else class="no-image">
                <van-icon name="photo-o" size="48" />
                <p>暂无现场图片</p>
              </div>
            </template>
          </van-cell>
        </van-cell-group>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { ImagePreview } from 'vant';

export default {
  name: 'ProcessFlowViewer',
  
  props: {
    // 批次号（必传）
    traceCode: {
      type: String,
      required: true,
      default: ''
    },
    // API 基础地址
    baseUrl: {
      type: String,
      default: `${process.env.VUE_APP_API_BASE_URL}/api`
    },
    // 是否自动加载
    autoLoad: {
      type: Boolean,
      default: true
    },
    // 轮播自动播放间隔（毫秒），设为0则不自动播放
    autoplay: {
      type: Number,
      default: 3000
    },
    // 自定义数据获取方法（可选，用于覆盖默认的API调用）
    fetchData: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      loading: true,
      recordList: [],
      imageCache: {},
      currentSwipeIndex: {},
      showPreview: false,
      currentImageList: [],
      previewIndex: 0,
      currentFlowId: ''
    };
  },

  watch: {
    traceCode: {
      handler(newVal) {
        if (newVal && this.autoLoad) {
          this.loadData();
        }
      },
      immediate: true
    }
  },

  methods: {
    // 加载数据（供外部调用）
    async loadData() {
      if (!this.traceCode) {
        console.warn('ProcessFlowViewer: traceCode is required');
        return;
      }
      
      this.loading = true;
      
      try {
        let records = [];
        
        // 使用自定义获取数据方法或默认API调用
        if (this.fetchData && typeof this.fetchData === 'function') {
          records = await this.fetchData(this.traceCode);
        } else {
          records = await this.fetchRecordsByTraceCode(this.traceCode);
        }
        
        this.recordList = records || [];
        
        // 预处理图片列表
        this.recordList.forEach(record => {
          this.imageCache[record.FlowId] = this.parseImages(record.ImagesBase64);
          this.currentSwipeIndex[record.FlowId] = 0;
        });
        
        // 触发加载完成事件
        this.$emit('loaded', this.recordList);
        
      } catch (error) {
        console.error('加载数据失败:', error);
        this.$emit('error', error);
        this.$toast?.fail('加载失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },

    // 默认API调用方法
    async fetchRecordsByTraceCode(traceCode) {
      const response = await axios.get(`${this.baseUrl}/Trace/GetWorkFlowDetails?traceCode=${traceCode}`);
      if (response.data && response.data.status == 0) {
        return response.data.data || [];
      }
      throw new Error(response.data?.message || '获取数据失败');
    },

    // 解析图片列表
    parseImages(imagesBase64) {
      if (!imagesBase64) return [];
      if (typeof imagesBase64 === 'string') {
        try {
          const images = JSON.parse(imagesBase64);
          return images.map(img => `data:image/png;base64,${img}`);
        } catch (e) {
          return [];
        }
      } else if (Array.isArray(imagesBase64)) {
        return imagesBase64.map(img => `data:image/png;base64,${img}`);
      }
      return [];
    },

    // 获取记录的图片列表
    getImageList(record) {
      return this.imageCache[record.FlowId] || [];
    },

    // 获取日期范围字符串
    getDateRange(record) {
      if (record.StartDate && record.EndDate) {
        return `${record.StartDate} 至 ${record.EndDate}`;
      }
      return '';
    },

    // 轮播切换事件
    onSwipeChange(flowId, index) {
      this.currentSwipeIndex[flowId] = index;
      this.$emit('swipe-change', { flowId, index });
    },

    // // 预览图片
    // previewImage(flowId, index) {
    //   this.currentFlowId = flowId;
    //   this.currentImageList = this.imageCache[flowId] || [];
    //   this.previewIndex = index;
    //   this.showPreview = true;
    //   this.$emit('preview', { flowId, index, imageList: this.currentImageList });
    // },

    // 预览切换事件
    onPreviewChange(index) {
      this.previewIndex = index;
    },

    // 刷新数据
    refresh() {
      this.loadData();
    },

    // 清空数据
    clear() {
      this.recordList = [];
      this.imageCache = {};
      this.currentSwipeIndex = {};
    },
    async previewImg(images, index){
      ImagePreview({
        images,      // 图片数组
        startPosition: index,  // 从哪张开始预览
        closeable: true,  // 显示关闭图标，用户手动点击关闭
      });
    }
  }
};
</script>

<style scoped>
.process-flow-viewer {
  background-color: #f5f5f5;
}

.loading {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* 批次号标题 */
.batch-header {
  background: linear-gradient(135deg, #1989fa, #0c6abf);
  color: white;
  padding: 20px 16px;
  margin-bottom: 12px;
}

.batch-header h2 {
  margin: 0 0 8px 0;
  font-size: 18px;
}

.batch-header p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

/* 记录卡片 */
.record-card {
  background-color: #fff;
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
}

.record-title {
  background-color: #f0f7ff;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.record-index {
  font-weight: bold;
  color: #1989fa;
  font-size: 16px;
}

.record-id {
  font-size: 12px;
  color: #666;
}

.cell-group {
  margin-bottom: 0;
}

.cell-group:deep(.van-cell-group__title) {
  padding: 12px 16px 4px;
  font-weight: 500;
}

/* 详细信息文本样式 */
.detail-text {
  padding: 8px 0;
  font-size: 14px;
  line-height: 1.8;
  color: #323233;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-title {
  font-weight: 500;
}

/* 图片轮播样式 */
.image-swipe-container {
  width: 100%;
  padding: 10px 0;
}

.image-swipe {
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
}

.swipe-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #000;
}

.swipe-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-index {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

/* 缩略图列表 */
.thumbnail-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.thumbnail-item {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
}

.thumbnail-item.active {
  border-color: #1989fa;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-more {
  width: 60px;
  height: 60px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
}

/* 无图片状态 */
.no-image {
  text-align: center;
  padding: 40px 0;
  color: #999;
}

.no-image p {
  margin-top: 8px;
  font-size: 14px;
}
</style>